import { useEffect, useRef, useState } from 'react';
import {
  alignHistoricalRevenuesToWizardOrder,
  hasFullHistoricalRevenues,
} from '../utils/qboHistoricalRevenues';

// useQboPrefill — drives the one-shot pre-fill of wizard inputs from the
// `qbo_synced_financials` GraphQL payload. Extracted from
// ValuationStudioWizard so the four jobs that used to run in a single
// useEffect (set form state, compute COGS/OpEx breakdown, build the
// `prefilled` flag map, build the legacy `qboSource` provenance object)
// stay testable and don't crowd the wizard component.
//
// The hook only runs once per `qboFinancials` reference: subsequent edits to
// individual fields by the user must not be clobbered by a re-fire.
//
// Returns:
//   applied      - boolean, true after the first successful pre-fill
//   prefilled    - { revenue, expenses, ownerComp, oneTimeExpenses, historicalRevenues } flag map
//   cogs         - QBO-reported COGS amount (number) — pure passthrough so
//                  the wizard can show the "X COGS + Y OpEx" hint
//   opex         - QBO-reported operating expenses amount (number)
//   source       - legacy provenance object { syncedAt, fields } kept around
//                  for the in-memory edit-delta UX. Real persistence lives
//                  on `result.data_sources` from the server (todo 125).
export default function useQboPrefill(qboFinancials, setters) {
  const {
    setRevenue,
    setExpenses,
    setOwnerComp,
    setOneTimeExpenses,
    setYear1Revenue,
    setYear2Revenue,
    setYear3Revenue,
    setGrowthOption,
  } = setters;

  const [applied, setApplied] = useState(false);
  const [prefilled, setPrefilled] = useState({});
  const [cogs, setCogs] = useState(0);
  const [opex, setOpex] = useState(0);
  const [source, setSource] = useState(null);
  const ranRef = useRef(false);

  useEffect(() => {
    if (!qboFinancials || ranRef.current) return;
    ranRef.current = true;

    const next = {};

    if (qboFinancials.revenue > 0) {
      setRevenue(String(Math.round(qboFinancials.revenue)));
      next.revenue = true;
    }

    const ebc = qboFinancials.expenses_by_category || qboFinancials.expensesByCategory || {};
    const opexAmt = ebc.operating_expenses || ebc.operatingExpenses || 0;
    const cogsAmt = ebc.cogs || 0;
    const totalOpex = opexAmt + cogsAmt;
    if (totalOpex > 0) {
      setExpenses(String(Math.round(totalOpex)));
      setCogs(cogsAmt);
      setOpex(opexAmt);
      next.expenses = true;
    }

    const ownerCompAmt = ebc.owner_compensation || ebc.ownerCompensation || 0;
    if (ownerCompAmt > 0) {
      setOwnerComp(String(Math.round(ownerCompAmt)));
      next.ownerComp = true;
    }

    const oneTime = ebc.one_time_expenses || ebc.oneTimeExpenses || 0;
    if (oneTime > 0) {
      setOneTimeExpenses(String(Math.round(oneTime)));
      next.oneTimeExpenses = true;
    }

    const historical = qboFinancials.historical_revenues || qboFinancials.historicalRevenues || [];
    if (hasFullHistoricalRevenues(historical)) {
      const [yr1, yr2, yr3] = alignHistoricalRevenuesToWizardOrder(historical);
      setYear1Revenue(String(Math.round(yr1)));
      setYear2Revenue(String(Math.round(yr2)));
      setYear3Revenue(String(Math.round(yr3)));
      setGrowthOption('calculate');
      next.historicalRevenues = true;
    }

    setPrefilled(next);
    setApplied(true);

    // Legacy provenance object — kept for in-memory edit-delta rendering on
    // the results page. The authoritative provenance now ships from the
    // server as `result.data_sources`; see DataSourcesPanel.
    const fields = {};
    if (qboFinancials.revenue > 0) fields.revenue = qboFinancials.revenue;
    if (cogsAmt > 0) fields.cogs = cogsAmt;
    if (opexAmt > 0) fields.operatingExpenses = opexAmt;
    if (ownerCompAmt > 0) fields.ownerCompensation = ownerCompAmt;
    if (oneTime > 0) fields.oneTimeExpenses = oneTime;
    if (hasFullHistoricalRevenues(historical)) {
      fields.historicalRevenues = alignHistoricalRevenuesToWizardOrder(historical);
    }
    setSource({
      syncedAt: qboFinancials.synced_at || qboFinancials.syncedAt || null,
      fields,
    });
  }, [qboFinancials]); // eslint-disable-line react-hooks/exhaustive-deps

  return { applied, prefilled, cogs, opex, source };
}
